import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { PageHeader } from '../ui/PageHeader';
import { StatCard } from '../ui/StatCard';
import { SkeletonCards } from '../ui/Skeleton';
import { ErrorBanner } from '../ui/ErrorBanner';
import { EmptyState } from '../ui/EmptyState';
import { CHART_COLORS } from '../lib/chartTheme';
import { timeAgo } from '../lib/timeAgo';
import {
  ACTIVE_WINDOW_DAYS,
  COHORT_WEEKS,
  DORMANT_WINDOW_DAYS,
  LAPSE_DAYS,
  SLIPPING_WINDOW_DAYS,
  computeEngagement,
  fetchEngagementSource,
  type Engagement,
  type LifecycleSegment,
  type ReadProbe,
  type StreakBucket,
} from '../lib/engagementMetrics';
import {
  ENGAGEMENT_DEFINITIONS,
  ENGAGEMENT_HANDOFF_PROMPT,
  ENGAGEMENT_PHASES,
  PLAN_STATUS_META,
  type PlanPhase,
} from '../data/engagementPlan';

/**
 * ENGAGEMENT — the lifecycle workstream's instrument panel.
 *
 * Evan (2026-08-14): "add an engagement page to admin dash for this with notes for how it should be
 * built out — i will build out in separate chat."
 *
 * Two jobs, deliberately on ONE page rather than a dashboard and a doc:
 *   1. Show what is actually true about people coming back, from real queries.
 *   2. Carry the build-out plan, so whoever builds streaks / nudges / digests / win-back is reading
 *      the plan and the baseline it was written against at the same time. A plan in a separate doc
 *      goes stale against the numbers within a week; this one cannot.
 *
 * ⚠️ EVERY NUMBER ABOVE THE PLAN IS COMPUTED FROM RATINGS, NOT APP OPENS, AND THAT IS FORCED.
 * The Instrumentation section exists to keep that visible — read the header comment in
 * `src/lib/engagementMetrics.ts` before trusting any session/event figure or promoting one here.
 *
 * Data access matches the rest of the dashboard exactly: the shared anon-key client under the
 * signed-in admin's session, so RLS does the authorisation. Where RLS refuses, the page probes and
 * SAYS SO rather than rendering a plausible zero — see `ProbeTable`.
 */

const TONE_BAR: Record<LifecycleSegment['tone'], string> = {
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
  accent: 'bg-accent',
  neutral: 'bg-borderStrong',
};

const TONE_DOT: Record<LifecycleSegment['tone'], string> = TONE_BAR;

function fmtPct(v: number | null): string {
  return v == null ? '—' : `${v}%`;
}

function fmtDays(v: number | null): string {
  return v == null ? '—' : `${v}d`;
}

/** Section wrapper — eyebrow + optional one-line "what this is for" note, used by every block. */
function Section({
  title,
  note,
  children,
}: {
  title: string;
  note?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-8 min-w-0">
      <div className="admin-eyebrow mb-2">{title}</div>
      {note ? <p className="mb-3 max-w-3xl text-[12px] leading-relaxed text-muted">{note}</p> : null}
      {children}
    </section>
  );
}

/** Horizontal distribution list — used for both streak distributions. */
function BucketBars({ buckets, unit }: { buckets: StreakBucket[]; unit: string }) {
  const max = buckets.reduce((a, b) => (b.users > a ? b.users : a), 0);
  if (!buckets.length) {
    return <EmptyState compact title="No runs yet" hint="Nobody has rated in a full week yet." />;
  }
  return (
    <div className="space-y-1.5">
      {buckets.map((b) => (
        <div key={b.len} className="flex items-center gap-3">
          <span className="w-16 shrink-0 text-right text-[11px] tabular-nums text-mutedDim">
            {b.len} {b.len === 1 ? unit : `${unit}s`}
          </span>
          <div className="h-2.5 min-w-0 flex-1 overflow-hidden rounded-sm bg-surfaceElevated">
            <div
              className="h-full rounded-sm bg-accent"
              style={{ width: max > 0 ? `${Math.max((b.users / max) * 100, 2)}%` : '0%' }}
            />
          </div>
          <span className="w-10 shrink-0 text-[11px] tabular-nums text-textPrimary">{b.users}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Live read-access probes. This renders the result of an ACTUAL attempted read, not a hard-coded
 * claim about permissions — so when the Phase 0 migrations land these rows flip on their own and
 * nobody has to remember to edit a doc.
 */
function ProbeTable({ probes }: { probes: ReadProbe[] }) {
  const tone = (s: ReadProbe['status']) =>
    s === 'readable'
      ? 'admin-status admin-status-success'
      : s === 'own_rows_only'
        ? 'admin-status admin-status-warning'
        : 'admin-status admin-status-danger';
  const label = (s: ReadProbe['status']) =>
    s === 'readable' ? 'Readable' : s === 'own_rows_only' ? 'Own rows only' : 'Blocked';

  return (
    <div className="admin-card !p-0 overflow-x-auto">
      <table className="admin-table admin-table--compact">
        <thead>
          <tr>
            <th>Source</th>
            <th>What it would measure</th>
            <th>Status</th>
            <th>Detail</th>
          </tr>
        </thead>
        <tbody>
          {probes.map((p) => (
            <tr key={p.table}>
              <td className="key whitespace-nowrap">{p.table}</td>
              <td className="text-muted">{p.what}</td>
              <td>
                <span className={tone(p.status)}>{label(p.status)}</span>
              </td>
              <td className="max-w-[34rem] text-[11.5px] leading-relaxed text-mutedDim">{p.detail}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** One phase of the build-out plan — collapsed to its `why`, expanded to the work and the traps. */
function PhaseCard({ phase }: { phase: PlanPhase }) {
  const [open, setOpen] = useState(phase.status === 'blocking');
  const meta = PLAN_STATUS_META[phase.status];
  return (
    <div className="admin-card !p-0 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-start gap-3 px-4 py-3.5 text-left transition-colors hover:bg-white/[0.03]"
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="admin-chip-id">{phase.order}</span>
            <span className="text-[13px] font-medium text-textPrimary">{phase.title}</span>
          </div>
          <p className="mt-1.5 text-[12px] leading-relaxed text-muted">{phase.why}</p>
        </div>
        <span className={`admin-status ${meta.tone} shrink-0`}>{meta.label}</span>
      </button>
      {open ? (
        <div className="space-y-4 border-t border-border/60 px-4 py-4">
          <div>
            <div className="admin-eyebrow mb-2">Build</div>
            <ul className="space-y-2">
              {phase.build.map((b, i) => (
                <li key={i} className="flex gap-2 text-[12px] leading-relaxed text-muted">
                  <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-accent/70" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="admin-eyebrow mb-2 !text-danger">Traps</div>
            <ul className="space-y-2">
              {phase.warnings.map((w, i) => (
                <li key={i} className="flex gap-2 text-[12px] leading-relaxed text-muted">
                  <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-danger/70" />
                  <span>{w}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="admin-eyebrow mb-1.5">Done when</div>
            <p className="text-[12px] leading-relaxed text-muted">{phase.acceptance}</p>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function EngagementScreen() {
  const [data, setData] = useState<Engagement | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    void (async () => {
      try {
        const src = await fetchEngagementSource();
        if (cancelled) return;
        setData(computeEngagement(src));
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : 'Failed to load engagement data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [nonce]);

  const copyPrompt = useCallback(() => {
    void navigator.clipboard
      .writeText(ENGAGEMENT_HANDOFF_PROMPT)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        /* clipboard unavailable — the prompt is selectable in the block below */
      });
  }, []);

  /** Only the last ~16 weeks are legible in the chart; older weeks stay in the cohort grid. */
  const chartWeeks = useMemo(() => (data ? data.weeks.slice(-16) : []), [data]);

  const activeCount = data?.segments.find((s) => s.key === 'active')?.count ?? 0;
  const slippingCount = data?.segments.find((s) => s.key === 'slipping')?.count ?? 0;
  const dormantCount = data?.segments.find((s) => s.key === 'dormant')?.count ?? 0;

  return (
    <div className="admin-page mx-auto max-w-6xl">
      <PageHeader
        title="Engagement"
        subtitle="Lifecycle — who comes back, who is slipping away, and what to build about it."
        actions={
          <button
            type="button"
            onClick={() => setNonce((n) => n + 1)}
            disabled={loading}
            className="admin-btn admin-btn-secondary admin-btn-sm"
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        }
      />

      {err ? <ErrorBanner className="mb-6">{err}</ErrorBanner> : null}
      {loading && !data ? <SkeletonCards cards={6} className="mb-8" /> : null}

      {data ? (
        <>
          {/* ── Headline ─────────────────────────────────────────────────── */}
          <div className="mb-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <StatCard label="Accounts" value={data.accounts} />
            <StatCard
              label="Activated"
              value={fmtPct(data.activationPct)}
              sub={`${data.raters.toLocaleString()} have rated at least once`}
            />
            <StatCard label="Active" value={activeCount} sub={`rated in ${ACTIVE_WINDOW_DAYS}d`} />
            <StatCard label="Slipping" value={slippingCount} sub={`${ACTIVE_WINDOW_DAYS + 1}–${SLIPPING_WINDOW_DAYS}d quiet`} />
            <StatCard label="Dormant" value={dormantCount} sub={`${SLIPPING_WINDOW_DAYS + 1}–${DORMANT_WINDOW_DAYS}d quiet`} />
            <StatCard
              label="On a 2+ week run"
              value={data.streaks.onRunNow}
              sub={`best ever ${data.streaks.bestEver} weeks`}
            />
          </div>

          {/* ── Lifecycle segments ───────────────────────────────────────── */}
          <Section
            title="Lifecycle"
            note={
              <>
                Every account, bucketed by days since its last rating. The two right-hand buckets have
                never rated at all — an activation problem, not a retention one, and mixing them into
                &ldquo;churned&rdquo; is how a lifecycle programme ends up sending win-back messages to
                people who never started.
              </>
            }
          >
            <div className="mb-3 flex h-3 w-full overflow-hidden rounded-sm border border-border">
              {data.segments.map((s) =>
                s.count > 0 ? (
                  <div
                    key={s.key}
                    className={TONE_BAR[s.tone]}
                    style={{ width: `${(s.count / Math.max(data.accounts, 1)) * 100}%` }}
                    title={`${s.label} — ${s.count}`}
                  />
                ) : null,
              )}
            </div>
            <div className="admin-card !p-0 overflow-x-auto">
              <table className="admin-table admin-table--compact">
                <thead>
                  <tr>
                    <th>Segment</th>
                    <th className="num">Users</th>
                    <th className="num">Share</th>
                    <th className="num">Median quiet</th>
                    <th>The play</th>
                  </tr>
                </thead>
                <tbody>
                  {data.segments.map((s) => (
                    <tr key={s.key}>
                      <td className="whitespace-nowrap">
                        <span className="inline-flex items-center gap-2">
                          <span className={`h-2 w-2 rounded-sm ${TONE_DOT[s.tone]}`} />
                          <span className="text-textPrimary">{s.label}</span>
                        </span>
                      </td>
                      <td className="num">{s.count.toLocaleString()}</td>
                      <td className="num text-mutedDim">{fmtPct(s.pct)}</td>
                      <td className="num text-mutedDim">{fmtDays(s.medianDaysSince)}</td>
                      <td className="max-w-[30rem] text-[11.5px] leading-relaxed text-muted">{s.play}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* ── Streaks ──────────────────────────────────────────────────── */}
          <Section
            title="Streaks — measured in weeks"
            note={
              <>
                The unit is a <strong className="text-textPrimary">week</strong>, not a day. Nobody goes
                out seven nights a week, so a daily streak would break every Monday and train people to
                ignore it. These runs exist with no streak feature shipped — the mechanic would be
                naming behaviour that is already there, which is the only kind worth shipping.
              </>
            }
          >
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div className="admin-card min-w-0">
                <div className="mb-1 text-[12px] font-medium text-textPrimary">Runs alive now</div>
                <p className="mb-3 text-[11px] leading-relaxed text-mutedDim">
                  A run counts as alive if its last rating week is this week or last week — the week in
                  progress is incomplete, so a Saturday-night habit is not broken on a Tuesday.
                </p>
                <BucketBars buckets={data.streaks.current} unit="week" />
              </div>
              <div className="admin-card min-w-0">
                <div className="mb-1 text-[12px] font-medium text-textPrimary">Longest run ever reached</div>
                <p className="mb-3 text-[11px] leading-relaxed text-mutedDim">
                  Across {data.streaks.ratersWithAnyRatingWeek.toLocaleString()} people who have rated at
                  least once. This is the pre-launch baseline for Phase&nbsp;1.
                </p>
                <BucketBars buckets={data.streaks.longest} unit="week" />
              </div>
            </div>
          </Section>

          {/* ── Win-back evidence ────────────────────────────────────────── */}
          <Section
            title="Win-back — does it even happen"
            note={
              <>
                Before building a win-back campaign, the question worth answering is whether people come
                back on their own at all, and after how long. They do — which both justifies the campaign
                and sets its send window. It is also the reason any campaign needs a permanent holdout:
                this organic rate would otherwise be credited to the send.
              </>
            }
          >
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              <StatCard
                label="Quiet right now"
                value={data.winback.currentlyLapsed}
                sub={`${LAPSE_DAYS}+ days since a rating — the addressable audience`}
              />
              <StatCard
                label="Have ever gone quiet"
                value={data.winback.everLapsed}
                sub="includes those still quiet"
              />
              <StatCard
                label="Came back unprompted"
                value={data.winback.resurrected}
                sub={`after a ${LAPSE_DAYS}+ day gap`}
              />
              <StatCard
                label="Organic resurrection"
                value={fmtPct(data.winback.resurrectionRate)}
                sub="the bar any campaign has to beat"
              />
              <StatCard
                label="Median return gap"
                value={fmtDays(data.winback.medianReturnGapDays)}
                sub={`longest ${fmtDays(data.winback.maxReturnGapDays)} — use this as the send offset`}
              />
            </div>
          </Section>

          {/* ── Weekly rhythm ────────────────────────────────────────────── */}
          <Section
            title="Weekly rhythm"
            note="Distinct people who rated each week, against ratings and signups. The gap between the bars and the line is depth per active user; the gap between signups and raters is the activation leak."
          >
            <div className="admin-card">
              <div className="h-[240px] w-full">
                {chartWeeks.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={chartWeeks} margin={{ top: 8, right: 8, bottom: 0, left: -18 }}>
                      <CartesianGrid stroke={CHART_COLORS.borderStrong} strokeDasharray="2 4" vertical={false} />
                      <XAxis
                        dataKey="label"
                        tick={{ fill: CHART_COLORS.mutedDim, fontSize: 10 }}
                        axisLine={{ stroke: CHART_COLORS.borderStrong }}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fill: CHART_COLORS.mutedDim, fontSize: 10 }}
                        axisLine={false}
                        tickLine={false}
                        width={40}
                      />
                      <Tooltip
                        cursor={{ fill: 'rgba(255,255,255,0.06)' }}
                        contentStyle={{
                          background: CHART_COLORS.surfaceElevated,
                          border: `1px solid ${CHART_COLORS.borderStrong}`,
                          borderRadius: 8,
                          fontSize: 12,
                        }}
                        labelStyle={{ color: CHART_COLORS.textPrimary }}
                        itemStyle={{ color: CHART_COLORS.mutedDim }}
                      />
                      <Bar
                        dataKey="ratings"
                        name="Ratings"
                        fill={CHART_COLORS.accent}
                        fillOpacity={0.35}
                        radius={[2, 2, 0, 0]}
                        maxBarSize={22}
                        /* Frozen for the same reason as the Overview charts — see HomeScreen's note
                           on recharts measuring a stale path during animation. */
                        isAnimationActive={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="raters"
                        name="People who rated"
                        stroke={CHART_COLORS.accent}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 3 }}
                        isAnimationActive={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="signups"
                        name="Signups"
                        stroke={CHART_COLORS.mutedDim}
                        strokeWidth={1.5}
                        strokeDasharray="3 3"
                        dot={false}
                        isAnimationActive={false}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyState title="No weekly activity yet" />
                )}
              </div>
            </div>
          </Section>

          {/* ── Cohort retention ─────────────────────────────────────────── */}
          <Section
            title="Cohort retention"
            note={
              <>
                Each row is a signup week; each cell is the share of that cohort who rated in week N
                after joining. Blank means the window has not closed yet — counting an unfinished week as
                a miss would make retention look like it collapses every time signups grow.
              </>
            }
          >
            <div className="admin-card !p-0 overflow-x-auto">
              <table className="admin-table admin-table--compact">
                <thead>
                  <tr>
                    <th>Joined</th>
                    <th className="num">Size</th>
                    {Array.from({ length: COHORT_WEEKS }, (_, i) => (
                      <th key={i} className="num">
                        W{i}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.cohorts.map((c) => (
                    <tr key={c.week}>
                      <td className="key whitespace-nowrap">{c.label}</td>
                      <td className="num text-mutedDim">{c.size}</td>
                      {c.cells.map((v, i) => (
                        <td
                          key={i}
                          className="num"
                          style={
                            v == null
                              ? undefined
                              : { backgroundColor: `rgba(139, 92, 246, ${Math.min(v / 100, 1) * 0.55})` }
                          }
                        >
                          {v == null ? <span className="text-mutedDim">·</span> : `${v}%`}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* ── Acquisition source ───────────────────────────────────────── */}
          <Section
            title="Where they came from vs whether they stayed"
            note="Self-reported at signup (profiles.referral_source). Channels do not retain equally — this is the crossbreak that says which acquisition work is worth repeating."
          >
            <div className="admin-card !p-0 overflow-x-auto">
              <table className="admin-table admin-table--compact">
                <thead>
                  <tr>
                    <th>Source</th>
                    <th className="num">Accounts</th>
                    <th className="num">Activated</th>
                    <th className="num">Activation</th>
                    <th className="num">Active in {SLIPPING_WINDOW_DAYS}d</th>
                    <th className="num">Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {data.sources.map((s) => (
                    <tr key={s.source}>
                      <td className="key">{s.source}</td>
                      <td className="num">{s.accounts.toLocaleString()}</td>
                      <td className="num text-mutedDim">{s.activated.toLocaleString()}</td>
                      <td className="num">{fmtPct(s.activatedPct)}</td>
                      <td className="num text-mutedDim">{s.active28.toLocaleString()}</td>
                      <td className="num">{fmtPct(s.active28Pct)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* ── Instrumentation ──────────────────────────────────────────── */}
          <Section
            title="Instrumentation — read this before trusting anything above"
            note={
              <>
                Everything on this page counts <strong className="text-textPrimary">ratings</strong>, not
                app opens, because the telemetry that would count app opens does not work yet. That
                under-counts engagement — browsing and leaving is invisible — but it never invents it,
                which is the trade a re-engagement programme has to take. The numbers below are the
                evidence, recomputed live.
              </>
            }
          >
            <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              <StatCard
                label="app_sessions rows"
                value={data.telemetry.sessionRows}
                sub={`${data.telemetry.sessionUsers} users · ${data.telemetry.sessionUsersWithOneRow} of them have exactly one row, ever`}
              />
              <StatCard
                label="Last session row"
                value={data.telemetry.lastSessionAt ? timeAgo(data.telemetry.lastSessionAt) : '—'}
                sub="a first-open marker, not a session log"
              />
              <StatCard
                label="app_events rows"
                value={data.telemetry.eventRows}
                sub={`${data.telemetry.eventUsers} users · ${data.telemetry.eventUsersWithOneActiveDay} have exactly one active day`}
              />
              <StatCard
                label="Last event"
                value={data.telemetry.lastEventAt ? timeAgo(data.telemetry.lastEventAt) : '—'}
              />
              <StatCard
                label="Rated after their last event"
                value={data.telemetry.ratersRatingNewerThanEvent}
                sub={`of ${data.telemetry.ratersComparable} raters with any event — proof the stream stops firing, rather than people stopping`}
              />
            </div>
            <ProbeTable probes={data.probes} />
          </Section>

          {/* ── The plan ─────────────────────────────────────────────────── */}
          <Section
            title="Build-out plan"
            note={
              <>
                The handoff for whoever builds this. Phases are ordered, and the order is the argument:
                nothing below Phase&nbsp;0 can be evaluated until return visits are measurable. Source of
                truth is <code className="text-muted">src/data/engagementPlan.ts</code> — edit it there, not
                in a doc, so the plan and the baseline it was written against stay in one place.
              </>
            }
          >
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <button type="button" onClick={copyPrompt} className="admin-btn admin-btn-primary admin-btn-sm">
                {copied ? 'Copied' : 'Copy handoff prompt'}
              </button>
              <span className="text-[11px] text-mutedDim">
                Paste into a fresh session — it carries the phase order, the traps, and the house rules.
              </span>
            </div>

            <div className="mb-6 space-y-3">
              {ENGAGEMENT_PHASES.map((p) => (
                <PhaseCard key={p.id} phase={p} />
              ))}
            </div>

            <div className="admin-card">
              <div className="admin-eyebrow mb-3">Definitions this page asserts</div>
              <dl className="space-y-2.5">
                {ENGAGEMENT_DEFINITIONS.map((d) => (
                  <div key={d.term} className="grid grid-cols-1 gap-1 sm:grid-cols-[11rem_1fr] sm:gap-3">
                    <dt className="text-[12px] font-medium text-textPrimary">{d.term}</dt>
                    <dd className="text-[12px] leading-relaxed text-muted">{d.meaning}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <details className="admin-card mt-4">
              <summary className="cursor-pointer text-[12px] font-medium text-textPrimary">
                Handoff prompt (full text)
              </summary>
              <pre className="mt-3 max-h-[420px] overflow-auto whitespace-pre-wrap rounded-lg bg-canvas px-3 py-3 text-[11.5px] leading-relaxed text-muted">
                {ENGAGEMENT_HANDOFF_PROMPT}
              </pre>
            </details>
          </Section>

          <p className="text-[11px] leading-relaxed text-mutedDim">
            Computed in one pass over {data.ratingsCounted.toLocaleString()} ratings and{' '}
            {data.accounts.toLocaleString()} accounts, fetched together — so every figure describes the
            same instant instead of disagreeing by however long a series of queries took. Median days to
            a first rating: {fmtDays(data.medianDaysToFirstRating)}; {fmtPct(data.sameDayActivationPct)} of
            raters rated on the day they joined. Days bucket at midnight America/Chicago, matching the
            Overview charts.
          </p>
        </>
      ) : null}
    </div>
  );
}
